import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getArchitectureTree, getProfilesByUlids } from '@/api/architecture';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { format } from 'date-fns';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import { useTracking } from '@/stores/tracking/tracking';
import {
  architectureDisplayName,
  getGroupArchitectureStats,
  getGroupTotalProfileCount,
  getFamilyArchitectureStats,
} from '@/components/jobArchitecture/helpers';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';
import { normalizeCustomFields } from './helpers';

export const useJobArchitecture = defineStore('jobArchitecture', () => {
  const { setLoading } = useAppLoading();
  const snackbar = useAppSnackbar();

  const treeData = ref(null);
  const selectedFamily = ref(null);
  const selectedFamilyProfiles = ref([]);

  // Set of expanded group node IDs
  const expandedGroupIds = ref(new Set());

  const xlsxDownloadLoading = ref(false);

  const selectedProfileUlids = ref([]);

  /** Saved list filter/search state when navigating to compare; cleared after restore on return. */
  const listFilterSnapshot = ref(null);

  /** Group/family tree node ids to expand when returning from compare (list view). */
  const listDefaultExpandedNodeIds = ref(null);

  const clearListDefaultExpandedNodeIds = () => {
    listDefaultExpandedNodeIds.value = null;
  };

  const setListDefaultExpandedNodeIds = (ids) => {
    listDefaultExpandedNodeIds.value = ids ?? null;
  };

  const setSelectedProfileUlids = (ulids) => {
    const next = Array.isArray(ulids) ? [...ulids] : [];

    selectedProfileUlids.value = next;

    useTracking().jaListSelectedProfiles({ count: next.length });

    if (next.length === 0) {
      clearListDefaultExpandedNodeIds();
    }
  };

  const clearSelectedProfileUlids = () => {
    setSelectedProfileUlids([]);
  };

  const setCompareSelectionUlids = (ulids) => {
    setSelectedProfileUlids(ulids);
  };

  const saveListFilterSnapshot = ({ fields, searchText }) => {
    listFilterSnapshot.value = {
      fields: fields.map((field) => ({
        id: field.id,
        value: field.value.map(({ id, label }) => ({ id, label })),
      })),
      searchText: searchText || '',
    };
  };

  const getAndClearListFilterSnapshot = () => {
    const snapshot = listFilterSnapshot.value;

    listFilterSnapshot.value = null;

    return snapshot;
  };

  const clearListFilterSnapshot = () => {
    listFilterSnapshot.value = null;
  };

  const loadTree = async () => {
    setLoading({ pageLoading: true });

    try {
      const response = await getArchitectureTree();

      treeData.value = response;

      return treeData.value;
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job Architecture load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const selectFamily = async (family) => {
    selectedFamily.value = family;
    selectedFamilyProfiles.value = [];

    if (!family) {
      return;
    }

    const ulids = (family.profiles || []).map((profile) => profile.ulid).filter(Boolean);

    setLoading({ listLoading: true });

    try {
      const profiles = ulids.length > 0 ? await getProfilesByUlids(ulids) : [];

      const normalized = (profiles || []).map((profile) => {
        const customFields = normalizeCustomFields(profile.custom_fields);

        return { ...profile, custom_fields: customFields };
      });

      selectedFamilyProfiles.value = normalized;
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job profiles load error: ${err.message || err}`,
      });

      selectedFamilyProfiles.value = [];
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const clearSelection = () => {
    selectedFamily.value = null;
    selectedFamilyProfiles.value = [];
  };

  const toggleGroupExpanded = (groupNodeId) => {
    const updatedExpandedGroupIds = new Set(expandedGroupIds.value);

    if (updatedExpandedGroupIds.has(groupNodeId)) updatedExpandedGroupIds.delete(groupNodeId);
    else updatedExpandedGroupIds.add(groupNodeId);

    expandedGroupIds.value = updatedExpandedGroupIds;
  };

  const downloadArchitectureCsv = async (tree) => {
    if (!tree?.groups?.length) return;

    xlsxDownloadLoading.value = true;

    const COLORS = {
      header: '#E0E0E0',
      group: '#C9D8E8',
      family: '#E2EBF3',
      profile: '#FFFFFF',
    };

    const BORDER = {
      borderStyle: 'thin',
      borderColor: '#BDBDBD',
    };

    const headerCell = (text) => ({
      value: text,
      fontWeight: 'bold',
      backgroundColor: COLORS.header,
      ...BORDER,
    });

    const boldCell = (value, bg) => ({
      value: value ?? '',
      fontWeight: 'bold',
      backgroundColor: bg,
      ...BORDER,
    });

    const profileCell = (value) => ({
      value: value ?? '',
      backgroundColor: COLORS.profile,
      ...BORDER,
    });

    try {
      // eslint-disable-next-line import/no-extraneous-dependencies
      const { default: writeExcelFile } = await import('write-excel-file/browser');

      const header = [
        'Job Family Group', 'Job Family', 'Job Profile', 'Job Code', 'Job Level',
        'Job Family Count', 'Job Profile Count', 'Employee Count', 'Alerts',
      ].map((h) => headerCell(h));

      const profileRows = (group, family) => (family.profiles ?? []).map((profile) => ([
        profileCell(architectureDisplayName(group.name)),
        profileCell(architectureDisplayName(family.name)),
        profileCell(profile.title),
        profileCell(Number(profile.hris_job_code) || profile.hris_job_code || ''),
        profileCell(formatJobLevel(profile.job_level, { mode: 'combined' })),
        profileCell(''),
        profileCell(''),
        profileCell(profile.employee_count || 0),
        profileCell(''),
      ]));

      const familyRows = (group) => (group.families ?? []).flatMap((family) => {
        const { profileCount: famProfiles, employeeCount: famEmployees } = getFamilyArchitectureStats(family);

        return [
          [
            boldCell(architectureDisplayName(group.name), COLORS.family),
            boldCell(architectureDisplayName(family.name), COLORS.family),
            boldCell('', COLORS.family),
            boldCell('', COLORS.family),
            boldCell('', COLORS.family),
            boldCell('', COLORS.family),
            boldCell(famProfiles, COLORS.family),
            boldCell(famEmployees, COLORS.family),
            boldCell('', COLORS.family),
          ],
          ...profileRows(group, family),
        ];
      });

      const dataRows = tree.groups.flatMap((group) => {
        const { familyCount, employeeCount: groupEmployees } = getGroupArchitectureStats(group);
        const profileCount = getGroupTotalProfileCount(group);

        return [
          [
            boldCell(architectureDisplayName(group.name), COLORS.group),
            boldCell('', COLORS.group),
            boldCell('', COLORS.group),
            boldCell('', COLORS.group),
            boldCell('', COLORS.group),
            boldCell(familyCount, COLORS.group),
            boldCell(profileCount, COLORS.group),
            boldCell(groupEmployees, COLORS.group),
            boldCell('', COLORS.group),
          ],
          ...familyRows(group),
        ];
      });

      const allRows = [header, ...dataRows];
      const colCount = header.length;

      const columns = Array.from({ length: colCount }, (_, colIdx) => {
        const maxLen = allRows.reduce((max, row) => {
          const cell = row[colIdx];
          const len = String(cell?.value ?? '').length;

          return Math.max(max, len);
        }, 0);
        return { width: maxLen + 2 };
      });

      const today = format(new Date(), READABLE_FILE_NAME_FORMAT);

      await writeExcelFile([header, ...dataRows], {
        columns,
      }).toFile(`job-architecture-table-${today}.xlsx`);
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job Architecture export error: ${err.message || err}`,
      });
    } finally {
      xlsxDownloadLoading.value = false;
    }
  };

  return {
    treeData,
    selectedFamily,
    selectedFamilyProfiles,
    expandedGroupIds,
    selectedProfileUlids,
    listFilterSnapshot,
    listDefaultExpandedNodeIds,
    loadTree,
    selectFamily,
    clearSelection,
    toggleGroupExpanded,
    xlsxDownloadLoading,
    downloadArchitectureCsv,
    setSelectedProfileUlids,
    clearSelectedProfileUlids,
    setListDefaultExpandedNodeIds,
    clearListDefaultExpandedNodeIds,
    setCompareSelectionUlids,
    saveListFilterSnapshot,
    getAndClearListFilterSnapshot,
    clearListFilterSnapshot,
  };
});
